import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Plus, GripVertical, Trash2, Edit, Eye, EyeOff,
  Type, Hash, Mail, Phone, ChevronDown, Calendar, Clock,
  CheckSquare, Circle, Link2, IndianRupee, ToggleLeft,
  FileText, AlignLeft, List, Loader2, Save, X, HelpCircle,
  Shield, Settings2, Columns, LayoutList
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const FIELD_TYPES = [
  { value: "text", label: "Text", icon: Type },
  { value: "textarea", label: "Textarea", icon: AlignLeft },
  { value: "number", label: "Number", icon: Hash },
  { value: "email", label: "Email", icon: Mail },
  { value: "phone", label: "Phone", icon: Phone },
  { value: "dropdown", label: "Dropdown", icon: ChevronDown },
  { value: "multiselect", label: "Multi-select", icon: List },
  { value: "date", label: "Date Picker", icon: Calendar },
  { value: "time", label: "Time Picker", icon: Clock },
  { value: "checkbox", label: "Checkbox", icon: CheckSquare },
  { value: "radio", label: "Radio Button", icon: Circle },
  { value: "file", label: "File Upload", icon: FileText },
  { value: "url", label: "URL", icon: Link2 },
  { value: "currency", label: "Currency", icon: IndianRupee },
  { value: "toggle", label: "Toggle (Yes/No)", icon: ToggleLeft },
];

const getFieldIcon = (type) => {
  const ft = FIELD_TYPES.find(t => t.value === type);
  return ft ? ft.icon : Type;
};

// ---- Field Config Dialog ----
function FieldConfigDialog({ open, onOpenChange, field, onSave, saving }) {
  const [form, setForm] = useState({});
  const [optionInput, setOptionInput] = useState("");

  useEffect(() => {
    if (field) {
      setForm({
        field_label: field.field_label || "",
        field_type: field.field_type || "text",
        is_required: field.is_required || false,
        is_active: field.is_active !== false,
        placeholder: field.placeholder || "",
        default_value: field.default_value || "",
        help_text: field.help_text || "",
        options: field.options || [],
        show_in_create: field.show_in_create !== false,
        show_in_edit: field.show_in_edit !== false,
        show_in_detail: field.show_in_detail !== false,
        show_in_table: field.show_in_table || false,
      });
    }
  }, [field]);

  const needsOptions = ["dropdown", "multiselect", "radio"].includes(form.field_type);

  const addOption = () => {
    if (!optionInput.trim()) return;
    setForm(p => ({ ...p, options: [...(p.options || []), optionInput.trim()] }));
    setOptionInput("");
  };

  const removeOption = (idx) => {
    setForm(p => ({ ...p, options: (p.options || []).filter((_, i) => i !== idx) }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{field?.field_id ? "Edit Field" : "Add New Field"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium mb-1.5 block">Field Label *</Label>
              <Input value={form.field_label} onChange={e => setForm(p => ({ ...p, field_label: e.target.value }))}
                placeholder="e.g. Budget Range" data-testid="field-label-input" />
            </div>
            <div>
              <Label className="text-xs font-medium mb-1.5 block">Field Type *</Label>
              <Select value={form.field_type} onValueChange={v => setForm(p => ({ ...p, field_type: v }))}>
                <SelectTrigger data-testid="field-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      <span className="flex items-center gap-2"><t.icon className="w-3.5 h-3.5" />{t.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs font-medium mb-1.5 block">Placeholder Text</Label>
            <Input value={form.placeholder} onChange={e => setForm(p => ({ ...p, placeholder: e.target.value }))}
              placeholder="Enter placeholder..." data-testid="field-placeholder-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium mb-1.5 block">Default Value</Label>
              <Input value={form.default_value} onChange={e => setForm(p => ({ ...p, default_value: e.target.value }))}
                placeholder="Default..." data-testid="field-default-input" />
            </div>
            <div>
              <Label className="text-xs font-medium mb-1.5 block">Help Text</Label>
              <Input value={form.help_text} onChange={e => setForm(p => ({ ...p, help_text: e.target.value }))}
                placeholder="Help for user..." data-testid="field-help-input" />
            </div>
          </div>

          {/* Options for dropdown/multiselect/radio */}
          {needsOptions && (
            <div>
              <Label className="text-xs font-medium mb-1.5 block">Options</Label>
              <div className="flex gap-2 mb-2">
                <Input value={optionInput} onChange={e => setOptionInput(e.target.value)}
                  placeholder="Add option..." className="flex-1"
                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addOption(); } }}
                  data-testid="field-option-input" />
                <Button size="sm" variant="outline" onClick={addOption} data-testid="add-option-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(form.options || []).map((opt, i) => (
                  <Badge key={i} variant="secondary" className="gap-1 pr-1">
                    {opt}
                    <button onClick={() => removeOption(i)} className="ml-0.5 hover:text-red-500"><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Visibility toggles */}
          <div className="border rounded-lg p-3 space-y-3">
            <p className="text-xs font-semibold text-slate-600 flex items-center gap-1.5">
              <Columns className="w-3.5 h-3.5" /> Show In
            </p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: "show_in_create", label: "Create Form" },
                { key: "show_in_edit", label: "Edit Form" },
                { key: "show_in_detail", label: "Details Page" },
                { key: "show_in_table", label: "Table View" },
              ].map(v => (
                <label key={v.key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch checked={form[v.key] || false}
                    onCheckedChange={c => setForm(p => ({ ...p, [v.key]: c }))} />
                  {v.label}
                </label>
              ))}
            </div>
          </div>

          {/* Required & Active */}
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch checked={form.is_required} onCheckedChange={c => setForm(p => ({ ...p, is_required: c }))}
                data-testid="field-required-toggle" />
              Required
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Switch checked={form.is_active} onCheckedChange={c => setForm(p => ({ ...p, is_active: c }))} />
              Active
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSave(form)} disabled={!form.field_label?.trim() || saving}
            className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-field-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {field?.field_id ? "Update" : "Create"} Field
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Live Preview ----
function LivePreview({ fields }) {
  const activeFields = fields.filter(f => f.is_active && f.show_in_create);
  return (
    <div className="space-y-4" data-testid="live-preview">
      <div className="flex items-center gap-2 mb-2">
        <Eye className="w-4 h-4 text-indigo-600" />
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">Live Preview — Create Lead Form</h3>
      </div>
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm">
        <div className="grid grid-cols-1 gap-4">
          {activeFields.map(field => {
            const Icon = getFieldIcon(field.field_type);
            return (
              <div key={field.field_id || field.field_key} className="space-y-1.5">
                <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                  {field.field_label}
                  {field.is_required && <span className="text-red-500">*</span>}
                  {field.help_text && <HelpCircle className="w-3 h-3 text-slate-400" />}
                </label>
                {field.field_type === "textarea" ? (
                  <div className="h-16 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600 px-3 py-2 text-xs text-slate-400">{field.placeholder || "Enter text..."}</div>
                ) : field.field_type === "toggle" ? (
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-4 bg-slate-300 rounded-full" />
                    <span className="text-xs text-slate-500">No</span>
                  </div>
                ) : field.field_type === "checkbox" ? (
                  <label className="flex items-center gap-2 text-sm">
                    <div className="w-4 h-4 border-2 border-slate-300 rounded" />
                    <span className="text-xs text-slate-500">{field.placeholder || field.field_label}</span>
                  </label>
                ) : ["dropdown", "multiselect", "radio"].includes(field.field_type) ? (
                  <div className="h-9 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600 px-3 flex items-center justify-between">
                    <span className="text-xs text-slate-400">{field.placeholder || `Select ${field.field_label}...`}</span>
                    <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                ) : field.field_type === "file" ? (
                  <div className="h-9 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-dashed border-slate-300 dark:border-slate-600 flex items-center justify-center">
                    <span className="text-xs text-slate-400">Click to upload file</span>
                  </div>
                ) : (
                  <div className="h-9 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600 px-3 flex items-center gap-2">
                    <Icon className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-xs text-slate-400">{field.placeholder || `Enter ${field.field_label.toLowerCase()}...`}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---- Integration Mapping Panel ----
const INTEGRATION_SOURCES = [
  {
    key: "facebook",
    label: "Facebook Lead Ads",
    color: "bg-blue-500",
    sampleFields: ["full_name", "email", "phone_number", "city", "state", "company_name", "job_title", "zip_code", "country", "street_address"],
  },
  {
    key: "google_sheets",
    label: "Google Sheets",
    color: "bg-green-500",
    sampleFields: ["name", "email", "phone", "company", "city", "notes", "source", "address", "website", "industry"],
  },
  {
    key: "email",
    label: "Email Sync",
    color: "bg-purple-500",
    sampleFields: ["from_name", "from_email", "subject", "body_text", "date", "reply_to"],
  },
  {
    key: "whatsapp",
    label: "WhatsApp",
    color: "bg-emerald-500",
    sampleFields: ["sender_name", "sender_phone", "message_text", "message_type", "timestamp"],
  },
  {
    key: "webhook",
    label: "Webhook / API",
    color: "bg-orange-500",
    sampleFields: ["name", "email", "phone", "company", "source", "notes", "custom_data"],
  },
];

function IntegrationMappingPanel({ fields, systemFields, mappingConfig, setMappingConfig, loading }) {
  if (loading) return (
    <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
  );

  const allTargetFields = [
    ...systemFields.map(f => ({ value: f.field_key, label: f.field_label, group: "System" })),
    ...fields.map(f => ({ value: f.field_key, label: f.field_label, group: "Custom" })),
  ];

  const updateMapping = (sourceType, sourceField, targetField) => {
    setMappingConfig(prev => {
      const updated = { ...prev };
      if (!updated[sourceType]) updated[sourceType] = {};
      if (targetField) {
        updated[sourceType][sourceField] = targetField;
      } else {
        delete updated[sourceType][sourceField];
      }
      return updated;
    });
  };

  return (
    <div className="space-y-6" data-testid="integration-mapping-panel">
      <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
        <p className="text-sm text-blue-700 dark:text-blue-400">
          <strong>Integration Field Mapping</strong> — Map incoming fields from integration sources to your CRM fields.
          When a lead comes from Facebook, Google Sheets, Email, WhatsApp, or Webhook, the system will auto-fill your custom fields based on these mappings.
          Unmapped fields will be stored in "Captured Data".
        </p>
      </div>

      {INTEGRATION_SOURCES.map(source => {
        const sourceMappings = mappingConfig[source.key] || {};
        return (
          <Card key={source.key} className="shadow-sm" data-testid={`mapping-card-${source.key}`}>
            <CardHeader className="pb-3 pt-4">
              <CardTitle className="text-sm flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${source.color}`} />
                {source.label}
                <Badge variant="outline" className="text-[10px] ml-1">
                  {Object.keys(sourceMappings).length} mapped
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-4">
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-3 pb-2 border-b border-slate-100 dark:border-slate-700">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Source Field</span>
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Maps To (CRM Field)</span>
                </div>
                {source.sampleFields.map(sf => (
                  <div key={sf} className="grid grid-cols-2 gap-3 items-center" data-testid={`mapping-row-${source.key}-${sf}`}>
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded font-mono text-slate-600 dark:text-slate-300">{sf}</code>
                    </div>
                    <Select value={sourceMappings[sf] || ""} onValueChange={v => updateMapping(source.key, sf, v || null)}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`mapping-select-${source.key}-${sf}`}>
                        <SelectValue placeholder="— Not mapped —" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value=" ">— Not mapped —</SelectItem>
                        {allTargetFields.map(tf => (
                          <SelectItem key={tf.value} value={tf.value}>
                            <span className="flex items-center gap-1.5">
                              {tf.group === "System" && <Shield className="w-3 h-3 text-amber-500" />}
                              {tf.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// ---- Main Component ----
export default function LeadFormBuilder() {
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Dialog state
  const [showFieldDialog, setShowFieldDialog] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Drag state
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const fetchFields = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/lead-fields`, { credentials: "include" });
      if (res.ok) setFields(await res.json());
    } catch (e) {
      toast.error("Failed to load fields");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFields(); }, [fetchFields]);

  // ---- CRUD Handlers ----
  const handleSaveField = async (formData) => {
    setSaving(true);
    try {
      const isEdit = editingField?.field_id;
      const url = isEdit ? `${API}/api/lead-fields/${editingField.field_id}` : `${API}/api/lead-fields`;
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        toast.success(isEdit ? "Field updated" : "Field created");
        setShowFieldDialog(false);
        setEditingField(null);
        fetchFields();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to save field");
      }
    } catch { toast.error("Error saving field"); }
    finally { setSaving(false); }
  };

  const handleDeleteField = async () => {
    if (!deleteConfirm) return;
    try {
      const res = await fetch(`${API}/api/lead-fields/${deleteConfirm.field_id}`, {
        method: "DELETE", credentials: "include"
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.had_data ? "Field and its data deleted" : "Field deleted");
        setDeleteConfirm(null);
        fetchFields();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to delete");
      }
    } catch { toast.error("Error deleting field"); }
  };

  const handleToggleActive = async (field) => {
    try {
      await fetch(`${API}/api/lead-fields/${field.field_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ is_active: !field.is_active })
      });
      fetchFields();
    } catch { toast.error("Failed to toggle field"); }
  };

  const handleToggleRequired = async (field) => {
    if (field.is_system && field.field_key === "name") return; // Name is always required
    try {
      await fetch(`${API}/api/lead-fields/${field.field_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ is_required: !field.is_required })
      });
      fetchFields();
    } catch { toast.error("Failed to update field"); }
  };

  // ---- Drag & Drop ----
  const handleDragStart = (idx) => { dragItem.current = idx; };
  const handleDragEnter = (idx) => { dragOverItem.current = idx; };
  const handleDragEnd = async () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    const copy = [...fields];
    const dragged = copy.splice(dragItem.current, 1)[0];
    copy.splice(dragOverItem.current, 0, dragged);

    const reordered = copy.map((f, i) => ({ ...f, sort_order: i }));
    setFields(reordered);

    dragItem.current = null;
    dragOverItem.current = null;

    // Save order
    try {
      await fetch(`${API}/api/lead-fields/reorder/batch`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(reordered.map((f, i) => ({ field_id: f.field_id, sort_order: i })))
      });
    } catch { toast.error("Failed to save order"); }
  };

  const openAdd = () => { setEditingField({ field_type: "text" }); setShowFieldDialog(true); };
  const openEdit = (field) => { setEditingField(field); setShowFieldDialog(true); };

  // Integration Mapping State
  const [builderTab, setBuilderTab] = useState("fields");
  const [mappingConfig, setMappingConfig] = useState({});
  const [loadingMapping, setLoadingMapping] = useState(false);
  const [savingMapping, setSavingMapping] = useState(false);

  const fetchMappingConfig = useCallback(async () => {
    setLoadingMapping(true);
    try {
      const res = await fetch(`${API}/api/lead-fields/mapping-config`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setMappingConfig(data.mappings || {});
      }
    } catch (e) { console.error(e); }
    finally { setLoadingMapping(false); }
  }, []);

  const handleSaveMapping = async () => {
    setSavingMapping(true);
    try {
      const res = await fetch(`${API}/api/lead-fields/mapping-config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ mappings: mappingConfig })
      });
      if (res.ok) toast.success("Field mapping saved");
      else toast.error("Failed to save mapping");
    } catch { toast.error("Error saving mapping"); }
    finally { setSavingMapping(false); }
  };

  useEffect(() => {
    if (builderTab === "mapping") fetchMappingConfig();
  }, [builderTab, fetchMappingConfig]);

  if (loading) return (
    <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>
  );

  const systemFields = fields.filter(f => f.is_system);
  const customFields = fields.filter(f => !f.is_system);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="lead-form-builder-page">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/settings")} data-testid="back-to-settings">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-slate-800 dark:text-white flex items-center gap-2">
                <LayoutList className="w-5 h-5 text-indigo-600" /> Lead Form Builder
              </h1>
              <p className="text-xs text-slate-500">Customize lead capture fields for your business</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {builderTab === "fields" && (
              <Button onClick={openAdd} className="bg-indigo-600 hover:bg-indigo-700 h-9" data-testid="add-field-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Add Field
              </Button>
            )}
            {builderTab === "mapping" && (
              <Button onClick={handleSaveMapping} disabled={savingMapping} className="bg-emerald-600 hover:bg-emerald-700 h-9" data-testid="save-mapping-btn">
                {savingMapping ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
                Save Mapping
              </Button>
            )}
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6 flex gap-0 -mb-px">
          {[
            { key: "fields", label: "Form Fields", icon: LayoutList },
            { key: "mapping", label: "Integration Mapping", icon: Link2 },
          ].map(tab => (
            <button key={tab.key} onClick={() => setBuilderTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                builderTab === tab.key
                  ? "border-indigo-600 text-indigo-700 dark:text-indigo-400"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
              }`}
              data-testid={`builder-tab-${tab.key}`}>
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-6">
        {builderTab === "fields" ? (
        <div className="grid lg:grid-cols-5 gap-6">
          {/* Field List - Left Panel */}
          <div className="lg:col-span-3 space-y-5">
            {/* System Fields */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3 pt-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Shield className="w-4 h-4 text-amber-500" /> System Fields
                  <Badge variant="outline" className="ml-1 text-[10px]">Cannot Delete</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 pb-4">
                {systemFields.map((field, idx) => (
                  <FieldRow key={field.field_id} field={field}
                    onEdit={() => openEdit(field)}
                    onToggleActive={() => handleToggleActive(field)}
                    onToggleRequired={() => handleToggleRequired(field)}
                    isSystem
                    draggable
                    onDragStart={() => handleDragStart(idx)}
                    onDragEnter={() => handleDragEnter(idx)}
                    onDragEnd={handleDragEnd}
                  />
                ))}
              </CardContent>
            </Card>

            {/* Custom Fields */}
            <Card className="shadow-sm">
              <CardHeader className="pb-3 pt-4">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Settings2 className="w-4 h-4 text-indigo-500" /> Custom Fields
                  <Badge className="ml-1 text-[10px] bg-indigo-100 text-indigo-700">{customFields.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pb-4">
                {customFields.length === 0 ? (
                  <div className="text-center py-10 border-2 border-dashed rounded-xl border-slate-200 dark:border-slate-700">
                    <Plus className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                    <p className="text-sm text-slate-500">No custom fields yet</p>
                    <p className="text-xs text-slate-400 mt-1">Click "Add Field" to create your first custom field</p>
                    <Button size="sm" variant="outline" className="mt-4" onClick={openAdd}>
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add Your First Field
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {customFields.map((field, idx) => (
                      <FieldRow key={field.field_id} field={field}
                        onEdit={() => openEdit(field)}
                        onDelete={() => setDeleteConfirm(field)}
                        onToggleActive={() => handleToggleActive(field)}
                        onToggleRequired={() => handleToggleRequired(field)}
                        draggable
                        onDragStart={() => handleDragStart(systemFields.length + idx)}
                        onDragEnter={() => handleDragEnter(systemFields.length + idx)}
                        onDragEnd={handleDragEnd}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Live Preview - Right Panel */}
          <div className="lg:col-span-2">
            <div className="sticky top-20">
              <Card className="shadow-sm">
                <CardContent className="pt-5">
                  <LivePreview fields={fields} />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
        ) : (
          /* Integration Mapping Tab */
          <IntegrationMappingPanel
            fields={fields.filter(f => !f.is_system && f.is_active)}
            systemFields={fields.filter(f => f.is_system)}
            mappingConfig={mappingConfig}
            setMappingConfig={setMappingConfig}
            loading={loadingMapping}
          />
        )}
      </div>

      {/* Add/Edit Field Dialog */}
      <FieldConfigDialog
        open={showFieldDialog}
        onOpenChange={(open) => { setShowFieldDialog(open); if (!open) setEditingField(null); }}
        field={editingField}
        onSave={handleSaveField}
        saving={saving}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Field</AlertDialogTitle>
            <AlertDialogDescription>
              Deleting "{deleteConfirm?.field_label}" will permanently remove this field and any data stored in it. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteField} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-field">
              Delete Field
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---- Field Row Component ----
function FieldRow({ field, onEdit, onDelete, onToggleActive, onToggleRequired, isSystem, draggable, onDragStart, onDragEnter, onDragEnd }) {
  const Icon = getFieldIcon(field.field_type);
  const typeLabel = FIELD_TYPES.find(t => t.value === field.field_type)?.label || field.field_type;

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border group transition-all ${
        !field.is_active
          ? "bg-slate-100 dark:bg-slate-800/30 border-slate-200/50 dark:border-slate-700/50 opacity-60"
          : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-600"
      }`}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnter={onDragEnter}
      onDragEnd={onDragEnd}
      onDragOver={e => e.preventDefault()}
      data-testid={`field-row-${field.field_key}`}
    >
      {/* Grip */}
      <div className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 flex-shrink-0">
        <GripVertical className="w-4 h-4" />
      </div>

      {/* Icon */}
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
        isSystem ? "bg-amber-50 dark:bg-amber-900/20" : "bg-indigo-50 dark:bg-indigo-900/20"
      }`}>
        <Icon className={`w-4 h-4 ${isSystem ? "text-amber-600" : "text-indigo-600"}`} />
      </div>

      {/* Label + Type */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">
          {field.field_label}
          {field.is_required && <span className="text-red-500 ml-0.5">*</span>}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[10px] text-slate-400 uppercase tracking-wider">{typeLabel}</span>
          {isSystem && <Badge variant="outline" className="text-[10px] h-4 px-1 border-amber-300 text-amber-600">System</Badge>}
          {!field.is_active && <Badge variant="outline" className="text-[10px] h-4 px-1 border-red-300 text-red-500">Hidden</Badge>}
        </div>
      </div>

      {/* Visibility indicators */}
      <div className="hidden sm:flex items-center gap-1.5">
        {field.show_in_create && <Badge variant="outline" className="text-[9px] h-4 px-1">Create</Badge>}
        {field.show_in_edit && <Badge variant="outline" className="text-[9px] h-4 px-1">Edit</Badge>}
        {field.show_in_table && <Badge variant="outline" className="text-[9px] h-4 px-1">Table</Badge>}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={onToggleRequired}
          className={`p-1.5 rounded-md text-xs ${field.is_required ? "text-red-500 bg-red-50" : "text-slate-400 hover:text-slate-600"}`}
          title={field.is_required ? "Make optional" : "Make required"}
          data-testid={`toggle-required-${field.field_key}`}>
          {field.is_required ? "Req" : "Opt"}
        </button>
        <button onClick={onToggleActive}
          className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700"
          title={field.is_active ? "Hide field" : "Show field"}
          data-testid={`toggle-active-${field.field_key}`}>
          {field.is_active ? <Eye className="w-3.5 h-3.5 text-green-500" /> : <EyeOff className="w-3.5 h-3.5 text-slate-400" />}
        </button>
        <button onClick={onEdit}
          className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-slate-700"
          data-testid={`edit-field-${field.field_key}`}>
          <Edit className="w-3.5 h-3.5 text-slate-500" />
        </button>
        {!isSystem && onDelete && (
          <button onClick={onDelete}
            className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20"
            data-testid={`delete-field-${field.field_key}`}>
            <Trash2 className="w-3.5 h-3.5 text-red-400 hover:text-red-600" />
          </button>
        )}
      </div>
    </div>
  );
}
